import { Alert, Col, Form, Row, Spin } from "antd";
import { useForm } from "antd/lib/form/Form";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "react-query";
import Button from "../../components/atoms/Button";
import DateRangePicker from "../../components/atoms/DateRangePicker";
import Explain from "../../components/atoms/Explain";
import PageLayout from "../../components/atoms/PageLayout";
import RadioGroup from "../../components/atoms/RadioGroup";
import TextArea from "../../components/atoms/TextArea";
import useModals from "../../hooks/useModals";
import { onlyReservationIntervalArray } from "../../shared/options";
import { ConfigFormValues, FormError } from "./Interface";
import DayOffInputs from "./components/DayOffInputs";
import HolidayInputs from "./components/HolidayInputs";
import InputWrapper from "./components/InputWrapper";
import MaximunVehicleCountInputs from "./components/MaximumVehicleCountInputs";
import PreviewModal from "./components/PreviewModal";
import ReservationTimeInputs from "./components/ReservationTimeInputs";
import RestTimeInputs from "./components/RestTimeInputs";
import SimultaneosVehicleCountInputs from "./components/SimultaneosVehicleCountInputs";
import { StyledSubTitle, StyledTitleWrapper } from "./styles";
import {
  checkDuplicatedDate,
  getConfigFormValues,
  makeRequestBody,
} from "./utils";

import {
  checkRepairShopBusiness,
  editRepairShopBusiness,
} from "../../apis/ReservationConfig/apis";
import { getRepairshopInfo } from "../../apis/UserInfo/apis";
import { ValidationType } from "../../components/atoms/Explain/types";
import ExplainWrapper from "../../components/molecures/ExplainWrapper";
import useBeforeUnload from "../../hooks/useBeforeUnload";
import useMouseDown from "../../hooks/useMouseDown";
import useShop from "../../hooks/useShop";
import useUser from "../../hooks/useUsers";
import { HttpError } from "../../network/types";
import {
  RepairShopSortType,
  ReservationInterval,
  TemporaryBusinessType,
} from "../../shared/enums";
import { alertError, alertSuccess } from "../../shared/utils";
import { getDecodeToken } from "../../shared/utils/Token";
import { strings } from "../../shared/utils/types";
import { theme } from "../../theme";
import color from "../../theme/color";
import CheckAvailableModal from "./components/CheckAvailableModal";
import HolidayReservationInputs from "./components/HolidayReservationInputs";
import HolidayRestInputs from "./components/HolidayRestInputs";
import MinusGhostButton from "./components/MinusGhostButton";
import PlusTimeTextButton from "./components/PlusTimeTextButton";
import TemporaryChangeInputs from "./components/TemporaryChangeInputs";
import {
  dayOffKeys,
  holidayRestTimeKeys,
  holidayTimeKeys,
  maximumTimeKeys,
  reservationTimeKeys,
  restTimeKeys,
  temporaryChangedBusinessKeys,
} from "./constants";

const { Item } = Form;

const ReservationConfig: React.FC = () => {
  const { shop } = useShop();
  const { getUserID } = useUser();
  const { openModal, openConfirmModal, closeModal } = useModals();
  const userId = getUserID();

  const divRef = useRef<{ [index: string]: HTMLDivElement | null }>({});
  const [form] = useForm<ConfigFormValues>();

  const [isTemporaryClosedOpen, setIsTemporaryClosedOpen] =
    useState<boolean>(false);
  const [isTemporaryChangedOpen, setIsTemporaryChangedOpen] =
    useState<boolean>(false);

  const {
    data: repairInfo,
    refetch,
    isFetching,
  } = useQuery(["ReservationInfo", shop], () => getRepairshopInfo(), {
    onError: (err: HttpError) => {
      alertError(err.message);
    },
  });

  const editRepairshopBusinessMutation = useMutation(editRepairShopBusiness, {
    onSuccess: () => {
      alertSuccess(strings.SUCCESS_MODIFY);
      refetch();
    },
    onError: (err: HttpError) => {
      alertError(err.message);
    },
  });

  const checkReservationAvailableMutation = useMutation(
    checkRepairShopBusiness,
    {
      onSuccess: (res) => {
        if (res.changeableBusiness !== true) {
          closeModal();
          if (id)
            openModal(
              <CheckAvailableModal
                preRequestBody={{
                  repairSortType: RepairShopSortType.ALL,
                  ...makeRequestBody(form.getFieldsValue(), id),
                }}
              />
            );
        } else {
          closeModal();
          editRepairshopBusinessMutation.mutate(
            makeRequestBody(form.getFieldsValue(), id)
          );
        }
      },
      onError: (err: HttpError) => {
        if (err.status === 404) {
          closeModal();
          editRepairshopBusinessMutation.mutate(
            makeRequestBody(form.getFieldsValue(), id)
          );
        }
        if (err.status !== 404) alertError(err.message);
      },
    }
  );
  const [id, setId] = useState<number>(0);
  const [errors, setErrors] = useState<FormError[]>([]);
  const repairshopInterval = Form.useWatch(
    ["repairShopBusiness", "reservationInterval"],
    form
  );

  const intervalExplainText = (timeInterval: ReservationInterval) => {
    switch (timeInterval) {
      case ReservationInterval.THIRTY:
        return "예약시간 예시 09:00, 09:30, 10:00...";
      case ReservationInterval.SIXTY:
        return "예약시간 예시 09:00, 10:00, 11:00...";
      case ReservationInterval.NINETY:
        return "예약시간 예시 09:00, 10:30, 12:00...";
      case ReservationInterval.ONEHUNDREDTWENTY:
        return "예약시간 예시 09:00, 11:00, 13:00...";
      case ReservationInterval.ONEHUNDREDFIFTY:
        return "예약시간 예시 09:00, 11:30, 14:00...";
      case ReservationInterval.ONEHUNDREDEIGHTY:
        return "예약시간 예시 09:00, 12:00, 15:00...";
      default:
        return "예약시간 예시 09:00, 09:30, 10:00...";
    }
  };
  const scrollToNull = (keys: string[]) => {
    for (
      let reservationKeyIndex = 0;
      reservationKeyIndex < reservationTimeKeys.length;
      reservationKeyIndex += 1
    ) {
      const reservationKey = reservationTimeKeys[reservationKeyIndex];
      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];
        if (key === "startTime" || key === "endTime") {
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
        if (key.includes(reservationKey))
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
      }
    }

    for (
      let restTimeKeyIndex = 0;
      restTimeKeyIndex < restTimeKeys.length;
      restTimeKeyIndex += 1
    ) {
      const restTimeKey = restTimeKeys[restTimeKeyIndex];
      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];
        if (key === "startTime" || key === "endTime") {
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
        if (key.includes(restTimeKey)) {
          return divRef.current.restTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
      }
    }
    for (
      let maximumTimeIndex = 0;
      maximumTimeIndex < maximumTimeKeys.length;
      maximumTimeIndex += 1
    ) {
      const maximumTimeKey = maximumTimeKeys[maximumTimeIndex];

      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];

        if (key.includes(maximumTimeKey))
          return divRef.current.maximumTime?.scrollIntoView({
            behavior: "smooth",
          });
      }
    }
    for (
      let dayOffKeyIndex = 0;
      dayOffKeyIndex < dayOffKeys.length;
      dayOffKeyIndex += 1
    ) {
      const dayOffKey = dayOffKeys[dayOffKeyIndex];
      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];
        if (key === "startTime" || key === "endTime") {
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
        if (key.includes(dayOffKey))
          return divRef.current.dayOffTime?.scrollIntoView({
            behavior: "smooth",
          });
      }
    }

    if (keys.includes("repairShopHolidayWorkdays"))
      return divRef.current.dayOffTime?.scrollIntoView({ behavior: "smooth" });
    for (
      let holidayBusinessIndex = 0;
      holidayBusinessIndex < holidayTimeKeys.length;
      holidayBusinessIndex += 1
    ) {
      const holidayTimeKey = holidayTimeKeys[holidayBusinessIndex];
      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];
        if (key === "startTime" || key === "endTime") {
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
        if (key.includes(holidayTimeKey))
          return divRef.current.holidayBusiness?.scrollIntoView({
            behavior: "smooth",
          });
      }
    }
    for (
      let holidayRestTimeIndex = 0;
      holidayRestTimeIndex < holidayRestTimeKeys.length;
      holidayRestTimeIndex += 1
    ) {
      const holidayRestTimeKey = holidayRestTimeKeys[holidayRestTimeIndex];

      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];
        if (key === "startTime" || key === "endTime") {
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
        if (key.includes(holidayRestTimeKey)) {
          return divRef.current.holidayRest?.scrollIntoView({
            behavior: "smooth",
          });
        }
      }
    }
    for (
      let temporaryChangeBusinessKeyIndex = 0;
      temporaryChangeBusinessKeyIndex < temporaryChangedBusinessKeys.length;
      temporaryChangeBusinessKeyIndex += 1
    ) {
      const temporaryChangedBusinessKey =
        temporaryChangedBusinessKeys[temporaryChangeBusinessKeyIndex];
      for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
        const key = keys[keyIndex];
        if (key === "startTime" || key === "endTime") {
          return divRef.current.reservationTime?.scrollIntoView({
            behavior: "smooth",
          });
        }
        if (key.includes(temporaryChangedBusinessKey)) {
          setIsTemporaryClosedOpen(true);
          setIsTemporaryChangedOpen(true);
          return divRef.current.temporaryChangedBusiness?.scrollIntoView({
            behavior: "smooth",
          });
        }
      }
    }

    return false;
  };

  const isWorkAtHoliday = Form.useWatch("isWorkAtHoliday", form);
  const temporaryCloseRanges = Form.useWatch("temporaryCloseRanges", form);
  const checkCloseDateStatus = (): ValidationType => {
    let flag = 0;
    if (temporaryCloseRanges) {
      const ranges = temporaryCloseRanges?.filter((val) => val?.length === 2);
      for (let i = 0; i < ranges?.length || 0; i += 1) {
        if (
          checkDuplicatedDate({
            target: {
              startedAt: ranges[i][0].toISOString(),
              endedAt: ranges[i][1].toISOString(),
            },
            times: ranges.map((val) => ({
              startedAt: val[0]?.toISOString(),
              endedAt: val[1]?.toISOString(),
            })),
          }).status === "error"
        )
          flag = 1;
        if (
          ranges[i][0]?.format("YYYY-MM-DD") ===
          ranges[i][1]?.format("YYYY-MM-DD")
        )
          flag = 0;
        if (flag === 1)
          return {
            status: "error",
            explain: "영업 중단 날짜는 서로 겹치게 설정할 수 없습니다.",
          };
      }
    }
    return {
      status: undefined,
      explain: undefined,
    };
  };

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (repairInfo) {
      setId(repairInfo.id as number);
      form.setFieldsValue(getConfigFormValues(repairInfo));

      // setAccountInfo(JSON.stringify(repairInfo)); -- 마스터계정을 위해 숨김
      setIsTemporaryClosedOpen(
        repairInfo.repairShopTemporaryBusinesses?.filter(
          (val) => val.temporaryBusinessType === TemporaryBusinessType.CLOSED
        )?.length > 0
      );
      setIsTemporaryChangedOpen(
        repairInfo.repairShopTemporaryBusinesses?.filter(
          (val) => val.temporaryBusinessType === TemporaryBusinessType.CHANGED
        )?.length > 0
      );

      form.resetFields();
    }
  }, [repairInfo]);

  const master = useMemo(() => {
    const info = getDecodeToken();

    return info?.isMaster && +shop === userId;
  }, [shop]);

  const handleMousedown = useCallback(
    (e: MouseEvent) => {
      const open = (e.target as HTMLElement).getAttribute("open");
      const tab = (e.target as HTMLElement).closest("[data-node-key]");

      if ((open !== undefined && open !== null) || tab) {
        if (form.isFieldsTouched()) {
          openConfirmModal({
            title: "변경사항 저장",
            contents: "변경사항을 저장하시겠습니까?",
            onCancel: () => {
              closeModal();
              ((tab as HTMLElement) || (e.target as HTMLElement)).click();
            },
            onOk: () => {
              checkReservationAvailableMutation
                .mutateAsync({
                  repairSortType: RepairShopSortType.ALL,
                  ...makeRequestBody(form.getFieldsValue(), id),
                })
                .then(() => {
                  closeModal();
                  (e.target as HTMLElement).click();
                });
            },
          });
        }
      }
    },
    [divRef]
  );

  useMouseDown(handleMousedown, [divRef]);

  useBeforeUnload(
    useCallback((e) => {
      if (form.isFieldsTouched()) {
        e.returnValue = "";
        return openConfirmModal({
          title: "변경사항 저장",
          contents: "변경사항을 저장하시겠습니까?",
          onCancel: () => {
            closeModal();
          },
          onOk: () => {
            checkReservationAvailableMutation
              .mutateAsync({
                repairSortType: RepairShopSortType.ALL,
                ...makeRequestBody(form.getFieldsValue(), id),
              })
              .then(() => {
                closeModal();
              });
          },
        });
      }
      return true;
    }, []),
    []
  );

  if (master)
    return (
      <PageLayout title="기본 예약정보">
        <div>
          <Alert
            message="마스터 계정으로 로그인 하신 상태입니다. 예약 설정을 변경하려면 개별 매장 계정으로 변경 후 진행해주세요."
            type="warning"
            showIcon
          />
        </div>
      </PageLayout>
    );

  return (
    <Spin
      spinning={
        isFetching ||
        editRepairshopBusinessMutation.isLoading ||
        checkReservationAvailableMutation.isLoading
      }
    >
      <Form
        form={form}
        {...(repairInfo && {
          initialValues: { ...getConfigFormValues(repairInfo) },
        })}
        onFinish={() => {
          openConfirmModal({
            contents: "매장 예약정보를 수정하시겠습니까?",
            title: "매장 예약정보 수정",
            onOk: () => {
              checkReservationAvailableMutation.mutate({
                repairSortType: RepairShopSortType.ALL,
                ...makeRequestBody(form.getFieldsValue(), id),
              });
            },
          });
        }}
        validateTrigger="onChange"
        scrollToFirstError
      >
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Row
              gutter={[8, 0]}
              style={{ width: "100%", position: "absolute", top: -56 }}
              justify="end"
            >
              <Col>
                <Button
                  type="default"
                  ghost
                  style={{ background: color.gray12 }}
                  onClick={() => {
                    if (id)
                      openModal(
                        <PreviewModal
                          preRequestBody={makeRequestBody(
                            form.getFieldsValue(),
                            id
                          )}
                        />
                      );
                  }}
                >
                  미리보기
                </Button>
              </Col>
              <Col>
                <Form.Item>
                  <Button
                    onClick={() => {
                      form.validateFields().catch((err) => {
                        const { errorFields } = err;
                        const errorKeys = errorFields.map((val: any) =>
                          val.name.length > 1 ? val.name[1] : val.name[0]
                        );
                        setErrors(
                          errorFields.map(
                            (val: { errors: string[]; name: string[] }) => ({
                              errors: val.errors,
                              name: val.name[0],
                            })
                          )
                        );

                        alertError(errorFields.map((value: any) => value.name));

                        scrollToNull(errorKeys);
                      });
                      form.submit();
                    }}
                  >
                    저장
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <PageLayout title="기본 예약정보">
              <InputWrapper title="예약 시간 간격">
                <Row gutter={[0, 12]}>
                  <Col span={24}>
                    <Explain
                      color={theme.colors.gray3}
                      status="question"
                      explain={intervalExplainText(repairshopInterval)}
                    />
                  </Col>
                  <Col span={24}>
                    <Item
                      noStyle
                      name={["repairShopBusiness", "reservationInterval"]}
                    >
                      <RadioGroup options={onlyReservationIntervalArray} />
                    </Item>
                  </Col>
                </Row>
              </InputWrapper>

              <InputWrapper
                ref={(ref) => {
                  divRef.current.reservationTime = ref;
                }}
                title={
                  <StyledTitleWrapper>
                    예약 시간 설정
                    <br />
                    <StyledSubTitle>(휴게시간 포함)</StyledSubTitle>
                  </StyledTitleWrapper>
                }
              >
                <ReservationTimeInputs form={form} errors={errors} />
              </InputWrapper>

              <InputWrapper
                ref={(ref) => {
                  divRef.current.restTime = ref;
                }}
                title="휴게시간"
                isOptional
              >
                <RestTimeInputs form={form} errors={errors} />
              </InputWrapper>

              <InputWrapper
                ref={(ref) => {
                  divRef.current.maximumTime = ref;
                }}
                title="일일 최대 예약 가능 대수"
              >
                <MaximunVehicleCountInputs form={form} errors={errors} />
              </InputWrapper>
              <InputWrapper title="동시간대 예약 가능 차량 수" hasLine={false}>
                <SimultaneosVehicleCountInputs form={form} errors={errors} />
              </InputWrapper>
            </PageLayout>
          </Col>
          <Col span={24}>
            <PageLayout title="휴무일 예약정보">
              <InputWrapper
                title="정기 휴무일"
                ref={(ref) => {
                  divRef.current.dayOffTime = ref;
                }}
              >
                <DayOffInputs form={form} errors={errors} />
              </InputWrapper>
              <InputWrapper hasLine={isWorkAtHoliday} title="공휴일 영업 여부">
                <HolidayInputs form={form} errors={errors} />
              </InputWrapper>
              <Item noStyle name="holidayRestTimes" />
              {isWorkAtHoliday === true && (
                <>
                  <InputWrapper
                    ref={(ref) => {
                      divRef.current.holidayBusiness = ref;
                    }}
                    title="공휴일 영업시간"
                  >
                    <HolidayReservationInputs form={form} errors={errors} />
                  </InputWrapper>
                  <InputWrapper
                    ref={(ref) => {
                      divRef.current.holidayRest = ref;
                    }}
                    title="공휴일 휴게시간"
                    hasLine={false}
                    isOptional
                  >
                    <HolidayRestInputs form={form} errors={errors} />
                  </InputWrapper>{" "}
                </>
              )}
            </PageLayout>
          </Col>
          <Col span={24}>
            <PageLayout
              title="영업 중단"
              hasButton
              onClickTitle={() => {
                setIsTemporaryClosedOpen(!isTemporaryClosedOpen);
              }}
              isOpen={isTemporaryClosedOpen}
            >
              <InputWrapper title="영업 중단 날짜" isOptional>
                <Item
                  noStyle
                  name="temporaryCloseRanges"
                  rules={[
                    {
                      validator: async () => {
                        if (checkCloseDateStatus().status === "error")
                          return Promise.reject(checkCloseDateStatus().explain);
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Row gutter={[8, 8]} wrap>
                    {(temporaryCloseRanges?.length === 0
                      ? [[]]
                      : temporaryCloseRanges
                    )?.map((val, i) => (
                      <Col span={24} key={`range${i}`}>
                        <Row wrap={false} gutter={[8, 0]}>
                          <Col>
                            <DateRangePicker
                              status={
                                checkCloseDateStatus().status === "error"
                                  ? "error"
                                  : undefined
                              }
                              style={{ maxWidth: "480px" }}
                              value={val ? [val[0], val[1]] : undefined}
                              onChange={(changeValues) => {
                                if (changeValues) {
                                  const newRanges = [
                                    ...form.getFieldValue(
                                      "temporaryCloseRanges"
                                    ),
                                  ];
                                  newRanges[i] = changeValues;
                                  form.setFieldsValue({
                                    temporaryCloseRanges: newRanges,
                                  });
                                } else {
                                  const newRanges = [
                                    ...form.getFieldValue(
                                      "temporaryCloseRanges"
                                    ),
                                  ];
                                  newRanges[i] = [];
                                  form.setFieldsValue({
                                    temporaryCloseRanges: newRanges,
                                  });
                                }
                              }}
                            />
                          </Col>
                          <Col>
                            <MinusGhostButton
                              disabled={val?.length === 0}
                              onClick={() => {
                                const newRanges = [
                                  ...form.getFieldValue("temporaryCloseRanges"),
                                ];
                                form.setFieldsValue({
                                  temporaryCloseRanges: newRanges.filter(
                                    (val, index) => i !== index
                                  ),
                                });
                              }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    ))}
                  </Row>
                </Item>
                <div style={{ marginTop: "12px" }}>
                  <PlusTimeTextButton
                    onClick={() => {
                      const newRanges = [
                        ...(form.getFieldValue("temporaryCloseRanges")
                          ?.length === 0
                          ? [[]]
                          : form.getFieldValue("temporaryCloseRanges")),
                      ];
                      newRanges.push([]);
                      form.setFieldsValue({
                        temporaryCloseRanges: newRanges,
                      });
                    }}
                    arrayLength={10}
                  >
                    날짜 추가
                  </PlusTimeTextButton>
                </div>
                <ExplainWrapper status={checkCloseDateStatus()} />
              </InputWrapper>
              <InputWrapper title="영업 중단 사유" isOptional hasLine={false}>
                <div style={{ width: "480px" }}>
                  <Item noStyle name="temporaryClosedReason">
                    <TextArea
                      style={{ maxWidth: "480px" }}
                      placeholder="사유를 입력해주세요"
                      showCount
                      maxLength={100}
                    />
                  </Item>
                </div>
              </InputWrapper>
            </PageLayout>
          </Col>
          <Col
            span={24}
            ref={(ref) => {
              divRef.current.temporaryChangedBusiness = ref;
            }}
          >
            <PageLayout
              title="일시적 운영 시간 변경"
              hasButton
              onClickTitle={() => {
                setIsTemporaryChangedOpen(!isTemporaryChangedOpen);
              }}
              isOpen={isTemporaryChangedOpen}
            >
              <TemporaryChangeInputs form={form} errors={errors} />
            </PageLayout>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default ReservationConfig;
